import React, { useEffect, useState } from 'react'

interface ProcessingModalProps {
  isVisible: boolean
  title: string
  message: string
  primaryColor?: string
  /**
   * Fully hide the page behind the modal. Use for phases that mount transient
   * composition DOM (print-resolution PrintPreview panes, mirrored photo clones)
   * which must never be guest-visible — a translucent blur backdrop let them
   * bleed through as duplicated/mirrored UI during strip generation (wave-1 audit).
   */
  opaque?: boolean
  /**
   * Escape hatch: after this many seconds a cancel button appears so a stalled
   * generation can't trap the guest with no way out. 0/undefined = never.
   */
  cancelAfterSeconds?: number
  cancelLabel?: string
  onCancel?: () => void
}

/**
 * Standardized processing modal component for consistent loading states across all pages.
 * 
 * Usage:
 * ```tsx
 * <ProcessingModal
 *   isVisible={isProcessing}
 *   title="Processing..."
 *   message="Please wait while we process your photos"
 *   primaryColor={primaryColor}
 * />
 * ```
 */
const ProcessingModal: React.FC<ProcessingModalProps> = ({
  isVisible,
  title,
  message,
  primaryColor = '#3B82F6', // Default blue color
  opaque = false,
  cancelAfterSeconds,
  cancelLabel,
  onCancel
}) => {
  const [showCancel, setShowCancel] = useState(false)

  useEffect(() => {
    setShowCancel(false)
    if (!isVisible || !cancelAfterSeconds || !onCancel) return undefined
    const timer = setTimeout(() => setShowCancel(true), cancelAfterSeconds * 1000)
    return (): void => clearTimeout(timer)
  }, [isVisible, cancelAfterSeconds, onCancel])

  if (!isVisible) return null

  return (
    <div
      className={`fixed inset-0 z-[210] flex items-center justify-center p-5 sm:p-8 ${
        opaque ? 'bg-gray-100' : 'bg-black/50 backdrop-blur-sm'
      }`}
    >
      <div className="w-full max-w-md rounded-2xl bg-white/95 p-6 shadow-2xl sm:p-8">
        <div className="text-center">
          <div
            className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-t-transparent"
            style={{
              borderColor: primaryColor,
              borderTopColor: 'transparent'
            }}
          />
          <h2 className="mb-2 text-xl font-bold text-gray-800 sm:text-2xl">{title}</h2>
          <p className="text-sm text-gray-600 sm:text-base">{message}</p>
          {showCancel && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="mt-5 min-h-[56px] w-full rounded-xl border border-gray-300 px-6 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-100"
            >
              {cancelLabel || 'Continue without waiting'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProcessingModal

